import { createContext, useState, useEffect, useRef, useCallback, ReactNode, useMemo } from 'react';
import { User as DbUser, TeamMember, Team } from '@shared/schema';
import AuthLoading from '@/components/auth/AuthLoading';
import { setUnauthorizedHandler, setForbiddenHandler } from '@/lib/queryClient';
import { toast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import {
  isAdminRole,
  isSuperAdminRole,
  resolveUserPermissions,
  hasResolvedPermission,
  getSystemRolePermissions,
  getTeamPositionPermissions,
} from '@shared/roleAccessConfig';

export interface TeamMembership {
  id: number;
  teamId: number;
  userId: number;
  position: 'team_head' | 'team_lead' | 'member';
  joinedAt: Date;
  team: Team;
}

export interface AuthContextType {
  user: DbUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  setUser: (user: DbUser | null) => void;
  teamMemberships: TeamMembership[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isTeamHead: boolean;
  isTeamLead: boolean;
  managedTeamIds: number[];
  leadTeamIds: number[];
  canManageLeaveHr: boolean;
  canViewOrgAnalytics: boolean;
  canManageContent: boolean;
  isManager: boolean;
  /** V2: Check if user has a permission via system role OR any team position */
  hasPermission: (permission: string) => boolean;
  /** V2: Check if user has a permission for a specific team (team-scoped check) */
  hasTeamPermission: (permission: string, teamId?: number) => boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  logout: async () => {},
  setUser: () => {},
  teamMemberships: [],
  isAdmin: false,
  isSuperAdmin: false,
  isTeamHead: false,
  isTeamLead: false,
  managedTeamIds: [],
  leadTeamIds: [],
  canManageLeaveHr: false,
  canViewOrgAnalytics: false,
  canManageContent: false,
  isManager: false,
  hasPermission: () => false,
  hasTeamPermission: () => false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<DbUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [teamMemberships, setTeamMemberships] = useState<TeamMembership[]>([]);
  const [customRolePermissions, setCustomRolePermissions] = useState<string[]>([]);

  // Combined fetch: try /api/user/me first (user + memberships in one request).
  // Falls back to /api/user if the combined endpoint fails for a non-auth reason.
  useEffect(() => {
    const fetchAuth = async () => {
      setIsLoading(true);

      try {
        const response = await fetch('/api/user/me', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setTeamMemberships(data.teamMemberships || []);
          setCustomRolePermissions(data.customRolePermissions || []);
          setIsLoading(false);
          return;
        }

        // 401 means not logged in — no fallback needed
        if (response.status === 401) {
          setUser(null);
          setTeamMemberships([]);
          setIsLoading(false);
          return;
        }

        // Any other error (500, etc.) — fall back to old /api/user
        const fallback = await fetch('/api/user', {
          credentials: 'include'
        });

        if (fallback.ok) {
          const userData = await fallback.json();
          setUser(userData);
          // Team memberships will be empty until next login, but user isn't locked out
        } else {
          setUser(null);
        }
        setTeamMemberships([]);
      } catch (error) {
        console.error('Error fetching user:', error);
        setUser(null);
        setTeamMemberships([]);
      }

      setIsLoading(false);
    };

    fetchAuth();
  }, []);

  const handleSetUser = (newUser: DbUser | null) => {
    setUser(newUser);
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {});

      setUser(null);
      setTeamMemberships([]);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Global 401 handler — auto-logout on session expiry.
  // Uses a ref to avoid re-registering on every render and to debounce multiple 401s.
  const sessionExpiredRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const handleSessionExpired = useCallback(() => {
    // Only trigger if user is currently logged in and we haven't already fired
    if (!userRef.current || sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;

    // Clear state locally — don't call /api/logout since the session is already dead
    setUser(null);
    setTeamMemberships([]);
    queryClient.clear();

    toast({
      title: "Session Expired",
      description: "Your session has expired. Please log in again.",
      variant: "destructive",
    });

    // Reset flag after a short delay so future sessions can also trigger this
    setTimeout(() => {
      sessionExpiredRef.current = false;
    }, 3000);
  }, []);

  // Global 403 handler — show a non-intrusive warning toast on permission denial.
  // Debounced so a burst of 403s does not spam the user (ignore repeats within 3s).
  const lastForbiddenAtRef = useRef(0);

  const handleForbidden = useCallback((message: string) => {
    const now = Date.now();
    if (now - lastForbiddenAtRef.current < 3000) return;
    lastForbiddenAtRef.current = now;

    toast({
      title: "Limited access",
      description: message,
    });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleSessionExpired);
    setForbiddenHandler(handleForbidden);
    return () => {
      setUnauthorizedHandler(null);
      setForbiddenHandler(null);
    };
  }, [handleSessionExpired, handleForbidden]);

  // Refresh permissions on tab focus — detects role changes made by admins
  // without requiring the user to log out and back in.
  useEffect(() => {
    const refreshPermissions = async () => {
      if (!userRef.current) return;
      try {
        const response = await fetch('/api/user/me', { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        if (!data.user) return;
        // Detect role change
        if (data.user.role !== userRef.current.role) {
          setUser(data.user);
          setTeamMemberships(data.teamMemberships || []);
          toast({
            title: "Permissions Updated",
            description: "Your role has been changed. The page will reflect your new permissions.",
          });
        }
      } catch {
        // Silently fail — user stays with current permissions
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Computed values
  const isAdmin = useMemo(() => isAdminRole(user?.role), [user]);
  const isSuperAdmin = useMemo(() => isSuperAdminRole(user?.role), [user]);

  const isTeamHead = useMemo(() =>
    teamMemberships.some(tm => tm.position === 'team_head'),
    [teamMemberships]
  );

  const isTeamLead = useMemo(() =>
    teamMemberships.some(tm => tm.position === 'team_lead'),
    [teamMemberships]
  );

  const managedTeamIds = useMemo(() =>
    teamMemberships
      .filter(tm => tm.position === 'team_head')
      .map(tm => tm.teamId),
    [teamMemberships]
  );

  const leadTeamIds = useMemo(() =>
    teamMemberships
      .filter(tm => tm.position === 'team_lead')
      .map(tm => tm.teamId),
    [teamMemberships]
  );

  const canManageLeaveHr = useMemo(() =>
    isAdminRole(user?.role) || user?.canManageLeaveHr === true,
    [user]
  );

  const canViewOrgAnalytics = useMemo(() =>
    isAdminRole(user?.role) || user?.canViewOrgAnalytics === true,
    [user]
  );

  const canManageContent = useMemo(() =>
    isAdminRole(user?.role) || user?.canManageContent === true,
    [user]
  );

  const isManager = useMemo(() =>
    isAdmin || isTeamHead || isTeamLead,
    [isAdmin, isTeamHead, isTeamLead]
  );

  // V2: Shared permission resolution — same function used by backend AI agent
  const resolved = useMemo(() => {
    if (!user) return null;
    const memberships = teamMemberships.map(tm => ({
      teamId: tm.teamId,
      position: tm.position,
    }));
    return resolveUserPermissions(user.role, memberships, customRolePermissions);
  }, [user?.role, teamMemberships, customRolePermissions]);

  // V2 permission check: uses shared resolveUserPermissions result
  const hasPermission = useCallback((permission: string): boolean => {
    if (!resolved) return false;
    return hasResolvedPermission(resolved, permission);
  }, [resolved]);

  // V2 team-scoped permission check: org-wide sources match any team,
  // team position sources only match if user holds position in that team
  const hasTeamPermission = useCallback((permission: string, teamId?: number): boolean => {
    if (!user || !resolved) return false;
    if (resolved.isAdmin) return true;
    // Org-wide sources: system role + custom roles (cover all teams)
    if (getSystemRolePermissions(user.role).includes(permission)) return true;
    if (customRolePermissions.includes(permission)) return true;
    // Team position check, optionally filtered to a specific team
    for (const tm of teamMemberships) {
      if (teamId !== undefined && tm.teamId !== teamId) continue;
      if (getTeamPositionPermissions(tm.position).includes(permission)) return true;
    }
    return false;
  }, [user?.role, resolved, customRolePermissions, teamMemberships]);

  if (isLoading) {
    return <AuthLoading />;
  }

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        logout,
        setUser: handleSetUser,
        teamMemberships,
        isAdmin,
        isSuperAdmin,
        isTeamHead,
        isTeamLead,
        managedTeamIds,
        leadTeamIds,
        canManageLeaveHr,
        canViewOrgAnalytics,
        canManageContent,
        isManager,
        hasPermission,
        hasTeamPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
